import { useState, useEffect } from "react";
import { Heart, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { listingsAPI } from "@/lib/api/listings";

export interface Listing {
  id: string;
  image: string;
  title: string;
  price: string;
  location: string;
  postedAt: string;
  featured?: boolean;
  description?: string;
}

export function ListingCard({ listing, compact = false }: { listing: Listing; compact?: boolean }) {
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    setIsLiked(listingsAPI.isLiked(listing.id));
  }, [listing.id]);

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const rawPrice = Number(listing.price.replace(/[^0-9]/g, ''));
      const compatibleListing = {
        id: listing.id,
        imageUrl: listing.image,
        title: listing.title,
        price: rawPrice,
        location: listing.location,
        createdAt: new Date().toISOString(),
        description: listing.description,
      };
      const res = await listingsAPI.toggleLike(listing.id, compatibleListing);
      setIsLiked(res.liked);
    } catch (err) {
      console.error("Failed to toggle like on card:", err);
    }
  };

  return (
    <Link
      to={`/listings/${listing.id}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        <img
          src={listing.image}
          alt={listing.title}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        {listing.featured && (
          <span className="absolute left-3 top-3 rounded-full bg-accent px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-accent-foreground shadow">
            Featured
          </span>
        )}
        <button
          aria-label={isLiked ? "Remove from saved" : "Save"}
          onClick={handleLike}
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-background/90 text-foreground backdrop-blur transition hover:bg-accent hover:text-accent-foreground z-10"
        >
          <Heart className={`h-4 w-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
        </button>
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3.5">
        <div className="font-heading text-lg font-bold text-foreground">{listing.price}</div>
        <h3 className="line-clamp-1 text-sm font-medium text-foreground">{listing.title}</h3>
        {!compact && listing.description && (
          <p className="line-clamp-1 text-xs text-muted-foreground">{listing.description}</p>
        )}
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {listing.location}
          </span>
          <span>{listing.postedAt}</span>
        </div>
      </div>
    </Link>
  );
}